import { useApolloClient, useLazyQuery } from "@apollo/client";
import { LOGIN_BASIC, LOGIN_MFA_CHALLENGE } from "api/query";
import { refetchUser } from "api/user";
import type {
  LoginMfaChallengeQuery,
  LoginMfaChallengeQueryVariables,
  LoginMfaQuery,
  LoginMfaQueryVariables,
} from "graphql/generated";
import { useStores } from "stores";
import { history } from "utils/history";

/**
 * Logs in with an email and a password, or with an MFA code after the first
 * step asked for one. `inProgress`, `twoFactorId` and `errors` stay in
 * `authStore`, because `AuthProvider` and the errorLink read them.
 */
export const useLogin = () => {
  const { authStore } = useStores();
  const client = useApolloClient();
  // Do not keep the credentials in the cache.
  const [loginBasic] = useLazyQuery<LoginMfaQuery, LoginMfaQueryVariables>(
    LOGIN_BASIC,
    { fetchPolicy: "no-cache" },
  );
  const [loginMfaChallenge] = useLazyQuery<
    LoginMfaChallengeQuery,
    LoginMfaChallengeQueryVariables
  >(LOGIN_MFA_CHALLENGE, { fetchPolicy: "no-cache" });

  const login = async ({
    email = "",
    password = "",
    authCode = "",
  }: {
    email?: string;
    password?: string;
    authCode?: string;
  }) => {
    authStore.setProgress(true);
    authStore.resetError();

    try {
      const { twoFactorId } = authStore;
      const { data, error } = twoFactorId
        ? await loginMfaChallenge({
            variables: { twoFactorId, code: authCode },
          })
        : await loginBasic({ variables: { email, password } });
      // `useLazyQuery` does not reject on a network error. Throw the error,
      // so that the login stays on the page, as with `client.query`.
      if (error?.networkError) throw error;
      const loginData = data?.loginMfa;
      if (loginData && "user" in loginData) {
        authStore.setAccount(loginData.user);
        // `AuthProvider` decides sign-in from `GET_USER`. Load it before the
        // navigation, so that the router sees the new session.
        await refetchUser(client);
        // Clear `twoFactorId` only after the load. If you clear `twoFactorId`
        // before the load, `LoginPage` shows the login form again until the
        // navigation.
        authStore.clearTwoFactorId();
        // `isAuthenticated` is false while a login is in progress.
        authStore.setProgress(false);
      }
      if (loginData && "twoFactorId" in loginData) {
        authStore.setTwoFactorId(loginData.twoFactorId);
      }
      history.push("/");
    } catch (e) {
      console.log(e);
    } finally {
      authStore.setProgress(false);
    }
  };

  return { login };
};
